<template>
  <div class="wrapper-container">
    <Loader :loading="isLoading" />

    <v-snackbar v-model="showSnackbar" :timeout="snackbarTimeout" top right color="red" text>
      {{ snackbarText }}
    </v-snackbar>

    <!-- First confirmation dialog: recipient needs to confirm their details. -->
    <v-dialog v-model="showRecipientConfirmationDialog" width="auto" persistent>
      <v-card>
        <v-card-title class="recipient-verification-title-text">
          Recipient Verification
        </v-card-title>
        <v-card-text class="recipient-verification-body-text">
          <v-divider></v-divider>
          <div class="recipient-verification-barcode-line">Item with barcode {{ parcelBarcode }} is addressed to:</div>
          <div class="recipient-verification-name-line">Name Surname: {{ confirmationFullName }}</div>
          <div class="recipient-verification-destination-line">Destination: {{ customerLocality }}</div>
          <div class="recipient-verification-question-line">Do your initials and item description agree with the above?
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" @click="closeConfirmationDialog(true)">Yes</v-btn>
          <v-btn class="recipient-verification-button-no" color="red" @click="showConfirmationForNo = true">No</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 2nd confirmation dialog: when the user does not confirm the recipient details. -->
    <v-dialog v-model="showConfirmationForNo" width="auto" persistent>
      <v-card>
        <v-card-title class="confirmation-for-no-title-text">
          Are you sure?
        </v-card-title>
        <v-card-text class="confirmation-for-no-body-text">
          <v-divider></v-divider>
          <div class="confirmation-for-no-details">If you select no, then the link in the email will be deactivated.
          </div>
          <div class="confirmation-for-no-details">For further clarification or to reactivate the link later</div>
          <div class="confirmation-for-no-details">you will need to contact customer service on <a
              href="mailto:info@maltapost.com">info@maltapost.com</a></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="success" @click="closeConfirmationDialog(false)">Confirm</v-btn>
          <v-btn class="recipient-verification-button-no" color="red"
            @click="showConfirmationForNo = false">Cancel</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Message: The user did not confirm the recipient details. -->
    <div class="wrapper-container"
      v-if="!isLoading && !showRecipientConfirmationDialog && parcelEligibleForRerouting && !recipientConfirmationAccepted && !errorFetchingPreDeliveryRequestData">
      <div class="wrapper-container">
        <div id="confirmation-submission-successful" class="wrapper-container">
          <font-awesome-icon id="success-check" class="check-icon" icon="circle-check" />
          <div class="submission-message">
            Recipient verification declined. Your response has been noted.
          </div>
        </div>
      </div>
    </div>

    <!-- Message: Unknown error when processing the GUID (e.g. TT throws an exception). -->
    <div id="error-container" class="wrapper-container" v-if="!isLoading && errorFetchingPreDeliveryRequestData">
      <font-awesome-icon class="error-icon" icon="link-slash" />

      <div class="message-container">
        <p class="header">
          <b>Oops!</b>
        </p>
        <div class="message">
          An error occurred while processing your request. Please ensure that you are using a valid link.
        </div>
      </div>
    </div>

    <!-- Message: User completed the re-routing process (happy path) -->
    <div class="wrapper-container" v-if="!isLoading && recipientConfirmationAccepted && confirmationSubmitted">
      <div id="confirmation-submission-successful" class="wrapper-container" v-if="confirmationSubmittedSuccessfully">
        <font-awesome-icon id="success-check" class="check-icon" icon="circle-check" />
        <div class="submission-message">
          Your selected delivery option has been submitted successfully.
        </div>
      </div>

      <div id="confirmation-submission-failure" class="wrapper-container" v-else>
        <font-awesome-icon id="failure-cross" class="cross-icon" icon="circle-exclamation" />
        <div class="submission-message">
          Failed to submit your selected delivery option.
        </div>
      </div>
    </div>

    <!-- Response from TrackAndTrace does not allow re-routing. -->
    <div class="wrapper-container"
      v-if="!isLoading && !parcelEligibleForRerouting && !errorFetchingPreDeliveryRequestData">

      <!-- Message: Re-routing no longer possible. -->
      <div id="parcel-rerouting-not-possible-container" class="wrapper-container" v-if="!requestAlreadySubmitted">
        <font-awesome-icon class="envelopes-icon" icon="envelopes-bulk" />

        <div class="message-container">
          <p class="header">
            <b>Oops!</b>
          </p>
          <div class="message">
            Parcel with barcode <b class="barcode">{{ parcelBarcode }}</b> is no longer eligible for rerouting as it is
            already being processed at the post office.
          </div>
        </div>
      </div>

      <!-- Message: Recipient already re-routed the item. -->
      <div id="parcel-rerouting-not-possible-container" class="wrapper-container" v-if="requestAlreadySubmitted">
        <font-awesome-icon class="envelopes-icon" icon="envelopes-bulk" />

        <div class="message-container">
          <p class="header">
            <b>Oops!</b>
          </p>
          <div class="message">
            The link is no longer valid because a response has already been submitted for the parcel with barcode <b
              class="barcode">{{ parcelBarcode }}</b>.
          </div>
        </div>

      </div>
    </div>

    <!-- Re-routing possible. (Happy path)-->
    <div id="parcel-rerouting-options-container" class="wrapper-container"
      v-if="!isLoading && parcelEligibleForRerouting && recipientConfirmationAccepted && !errorFetchingPreDeliveryRequestData && !confirmationSubmitted">
      <div class="eligibility-text-container">
        <font-awesome-icon id="eligibility-check" class="check-icon" icon="circle-check" />
        <div v-if="redirectionRequestIsForEasipikCustomer">
          Parcel with barcode <b class="barcode">{{ parcelBarcode }}</b> is eligible for delivery to your default Easipik parcel locker.</div>
        <div v-else>
          Parcel <b class="barcode">{{ parcelBarcode }}</b> is eligible for pick up.
        </div>
      </div>

      <div class="delivery-options-container">
        <div>
          <b>
            Hi, {{ customerFullName }}! Please choose your preferred delivery option for this parcel:
          </b>
        </div>

        <div id="parcel-locker-redirection" class="delivery-options" v-if="redirectionRequestIsForEasipikCustomer">
          <div class="delivery-option-wrapper">
            <input type="radio" id="parcel-locker-delivery" value="1" v-model="selectedDeliveryOption">
            <label class="delivery-option-label" for="parcel-locker-delivery">
              <div>
                <div class="delivery-option-title-text">
                  <b>Delivery to default Easipik parcel locker</b>
                </div>
                <span>
                  I confirm that I would like to receive my parcel at my default Easipik parcel locker location.
                </span>
                <br />
                <span class="address">
                  <em>{{ defaultParcelLockerName }}</em>
                </span>
                <div class="condition-text-container"><i>* If you want to change the locker location, we kindly ask
                    you to change your default locker from the EasiPik Portal, before proceeding with this
                    request.</i></div>
                <div class="condition-text-container"><i>** If the item is oversized (any of the package dimensions
                    are greater than 40x40x60 cm) or the preferred locker is full, this redirection request will not be handled.</i></div>
              </div>
            </label>
          </div>

          <br />

          <div class="delivery-option-wrapper">
            <input type="radio" id="original-address-delivery" value="2" v-model="selectedDeliveryOption">
            <label class="delivery-option-label" for="original-address-delivery">
              <div>
                <div class="delivery-option-title-text">
                  <b>Delivery to original physical address</b>
                </div>
                <span>
                  I confirm that I would like to maintain my parcel's original delivery address.
                </span>
                <br />
                <span class="address">
                  <em>{{ customerAddress }}</em>
                </span>
              </div>
            </label>
          </div>
        </div>

        <div id="post-office-redirection" class="delivery-options" v-else>
          <div class="delivery-option-wrapper" v-if="specialLocations && specialLocations.length">
            <input type="radio" id="special-location-delivery" value="1" v-model="selectedDeliveryOption">
            <label class="delivery-option-label" for="special-location-delivery">
              <div>
                <div class="delivery-option-title-text">
                  <b>Delivery to an express pick-up location</b>
                </div>
                <div>
                  I confirm that I would like my parcel delivered to one of the available express pick-up locations. 
                </div>
                <div class="note">
                  Parcel pick-up can be done on the day of its arrival, if parcel is received before <b>14:00</b> (local time).
                </div>
                <div class="special-locations-dropdown-wrapper">
                  <select id="special-locations-dropdown" 
                  v-model="selectedSpecialLocationCode" 
                  @change="handleSpecialLocationChange">
                    <option disabled value="">Select express pick-up location</option>
                    <option v-for="specialLocation in specialLocations" :key="specialLocation.postOfficeCode" :value="specialLocation.postOfficeCode">
                      {{ specialLocation.postOfficeName }}
                    </option>
                  </select>
                  <br />
                  <div class="missing-special-location-error-message-wrapper" v-if="!specialLocationSet">
                    <span>
                      <font-awesome-icon icon="circle-exclamation" />
                    </span>
                    <span>
                      Please select an express pick-up location from the above dropdown.
                    </span> 
                  </div>
                  <div class="address-info">
                    Click <a class="black-anchor" href="https://www.maltapost.com/postoffices" target="_blank" rel="noopener">here</a> to find the full address of your selected <b>express pick-up</b> location.
                  </div>
                </div>
              </div>
            </label>
          </div>

          <br />

          <div class="delivery-option-wrapper">
            <input type="radio" id="original-address-delivery" value="3" v-model="selectedDeliveryOption">
            <label class="delivery-option-label" for="original-address-delivery">
              <div>
                <div class="delivery-option-title-text">
                  <b>Delivery to original physical address</b>
                </div>
                <span>
                  I confirm that I would like to maintain my parcel's original delivery address.
                </span>
                <br />
                <span class="address">
                  <em>{{ customerAddress }}</em>
                </span>
              </div>
            </label>
          </div>
        </div>

        <br />

        <div class="note-emphasized collection-availability-note" v-if="!redirectionRequestIsForEasipikCustomer">
          <span class="info-icon-wrapper">
            <font-awesome-icon class="info-icon" icon="circle-info" />
          </span>
          <span>
            Please be informed that if you opt to redirect your parcel to an alternative pick-up location, an email will be sent out to you once the parcel is available for collection.            
          </span>     
        </div>

        <br />

        <v-btn type="button" color="primary" class="px-10 py-5 mb-5" @click="submitDeliveryOptionAsync()">
          Confirm delivery
        </v-btn>
      </div>

      <font-awesome-icon class="signs-icon" icon="signs-post" />
    </div>

  </div>

</template>

<script>
import Loader from './Loaders/Loader.vue';
import { createNamespacedHelpers } from "vuex";
import { validate as validateGuid } from 'uuid';
import preDeliveryRedirectionTypes from '../constants/preDeliveryRedirectionTypes';
import predeliveryOptions from '../constants/predeliveryOptions';
const { mapGetters } = createNamespacedHelpers("preDeliveryRedirection");

export default {
  name: 'PreDeliveryRedirection',
  data() {
    return {
      isLoading: false,
      parcelBarcode: null,
      customerFullName: null,
      confirmationFullName: null,
      customerLocality: null,
      customerAddress: null,
      trackedItemGuid: null,
      specialLocationSet: true,
      closestPostOfficeName: null,
      closestPostOfficeCode: null,
      closestPostOfficeIsSubPostOffice: false,
      selectedDeliveryOption: 1,
      selectedSpecialLocationCode: '',
      specialLocations: null,
      defaultParcelLockerCode: null,
      defaultParcelLockerName: null,
      parcelEligibleForRerouting: false,
      errorFetchingPreDeliveryRequestData: false,
      confirmationSubmitted: false,
      confirmationSubmittedSuccessfully: false,
      showRecipientConfirmationDialog: false,
      showConfirmationForNo: false,
      recipientConfirmationAccepted: false,
      requestAlreadySubmitted: false,
      redirectionRequestIsForEasipikCustomer: null
    };
  },
  components: {
    Loader
  },
  computed: {
    ...mapGetters([
      "snackbarTimeout",
      "snackbarText",
      "busy"
    ]),
    showSnackbar: {
      set(isSnackbarVisible) {
        this.$store.dispatch(
          "preDeliveryRedirection/setSnackbarVisibility",
          isSnackbarVisible
        );
      },
      get() {
        return this.$store.getters["preDeliveryRedirection/showSnackbar"];
      }
    },
  },
  async created() {
    const trackedItemGuidQueryParameter = this.$route.params.predeliveryRequestGuid;

    if (validateGuid(trackedItemGuidQueryParameter)) {
      this.trackedItemGuid = trackedItemGuidQueryParameter;

      this.isLoading = true;
      this.showRecipientConfirmationDialog = false;
      this.showConfirmationForNo = false;
      this.confirmationSubmitted = false;
      this.confirmationSubmittedSuccessfully = false;

      const result = await this.$store.dispatch("preDeliveryRedirection/getPreDeliveryRequestDetailsByGuidAsync", this.trackedItemGuid);

      if (result && !result.error && result.detailsRetrievedSuccessfully) {
        this.parcelBarcode = result.parcelBarcode;
        this.requestAlreadySubmitted = result.requestAlreadySubmitted;
        this.parcelEligibleForRerouting = result.parcelEligibleForRerouting;

        if (this.parcelEligibleForRerouting) {
          this.showRecipientConfirmationDialog = true;

          this.customerLocality = result.customerLocality;
          this.customerFullName = result.customerFullName;

          this.confirmationFullName = this.customerFullName
            .split(" ")
            .map(x => x[0] + '***' + (x.length > 1 ? x.slice(-1) : ''))
            .join(" ");

          this.customerAddress = [
            result.customerAddress1,
            result.customerAddress2,
            result.customerLocality,
            result.customerPostCode,
            result.customerCountry
          ].filter(Boolean).join(", ");
          
          this.redirectionRequestIsForEasipikCustomer = 
            result.preDeliveryRedirectionType === preDeliveryRedirectionTypes.EasipikCustomer;

          if (this.redirectionRequestIsForEasipikCustomer) {
            this.defaultParcelLockerCode = result.defaultParcelLockerCode;
            this.defaultParcelLockerName = result.defaultParcelLockerName;
          }
          else {
            this.specialLocations =  result.specialDeliveryLocations;

            this.closestPostOfficeCode = result.postOfficeCode;
            this.closestPostOfficeName = result.postOfficeName;  
            this.closestPostOfficeIsSubPostOffice = result.isSubPostOffice;                    
          }
        }
      }
      else {
        this.errorFetchingPreDeliveryRequestData = true;
      }
    }
    else {
      this.errorFetchingPreDeliveryRequestData = true;
    }

    this.isLoading = false;
  },
  methods: {
    handleSpecialLocationChange () {
      this.specialLocationSet = Boolean(this.selectedSpecialLocationCode);
    },
    closeConfirmationDialog: async function (recipientConfirmationAccepted) {
      this.showRecipientConfirmationDialog = false;
      this.showConfirmationForNo = false;
      this.recipientConfirmationAccepted = recipientConfirmationAccepted;

      if (!recipientConfirmationAccepted) {
        this.isLoading = true;

        const result = await this.$store.dispatch("preDeliveryRedirection/confirmPreDeliveryRequestSelectionAsync", {
          trackedItemGuid: this.trackedItemGuid,
          defaultLockerCode: null,
          recipientConfirmationAccepted,
          closestPostOfficeCode: null,
          selectedSpecialLocationCode: null,
          chosenPredeliveryOption: null
        });

        this.isLoading = false;

        if (result.error) { return; }
      }

      this.showRecipientConfirmationDialog = false;
      this.showConfirmationForNo = false;
      this.recipientConfirmationAccepted = recipientConfirmationAccepted;
    },
    submitDeliveryOptionAsync: async function () {
      this.selectedDeliveryOption = parseInt(this.selectedDeliveryOption, 10);
      
      // Set to 'true' if redirection to a default locker is accepted (EP customers only)
      const redirectionToLockerAccepted = this.redirectionRequestIsForEasipikCustomer && (this.selectedDeliveryOption === 1);

      // Set to 'true' if redirection to a special location is accepted (non-EP customers only)
      const redirectionToSpecialLocationAccepted = !this.redirectionRequestIsForEasipikCustomer && (this.selectedDeliveryOption === 1);

      // Set to 'true' if redirection to closest PO is accepted (non-EP customers only)
      const redirectionToClosestPostOfficeAccepted = !this.redirectionRequestIsForEasipikCustomer && (this.selectedDeliveryOption === 2); 

      // If redirection is to a special location and no special location is selected
      if (redirectionToSpecialLocationAccepted && !this.selectedSpecialLocationCode) {
        this.specialLocationSet = false;
        return;
      }

      const chosenPredeliveryOption = 
        (redirectionToLockerAccepted ? predeliveryOptions.RedirectToLocker : 
          (redirectionToSpecialLocationAccepted ? predeliveryOptions.RedirectToSpecialLocation :
            (redirectionToClosestPostOfficeAccepted ? predeliveryOptions.RedirectToClosestPO : predeliveryOptions.DeliverToOriginalAddress)));

      this.isLoading = true;

      const closestPostOfficeCode = (redirectionToClosestPostOfficeAccepted ? this.closestPostOfficeCode : null);
      const selectedSpecialLocationCode = (redirectionToSpecialLocationAccepted ? this.selectedSpecialLocationCode : null);

      const result = await this.$store.dispatch("preDeliveryRedirection/confirmPreDeliveryRequestSelectionAsync", {
        trackedItemGuid: this.trackedItemGuid,
        defaultLockerCode: this.defaultParcelLockerCode,
        recipientConfirmationAccepted: true,
        closestPostOfficeCode,
        selectedSpecialLocationCode,
        chosenPredeliveryOption
      });

      this.confirmationSubmitted = true;

      this.confirmationSubmittedSuccessfully =
        (result && !result.error && result.submittedSuccessfully);

      this.isLoading = false;
    }
  }
};
</script>

<style scoped>
.wrapper-container {
  width: 100%;
  height: 100%;
  display: flex;
}

.wrapper-container#error-container,
.wrapper-container#parcel-rerouting-not-possible-container {
  column-gap: 38px;
  align-items: center;
  justify-content: center;
}

.wrapper-container#parcel-rerouting-options-container {
  padding: 52px;
  font-size: 18px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.wrapper-container#confirmation-submission-successful,
.wrapper-container#confirmation-submission-failure {
  display: flex;
  padding: 98px;
  row-gap: 24px;
  align-items: center;
  flex-direction: column;
}

.envelopes-icon,
.check-icon,
.cross-icon,
.signs-icon,
.error-icon {
  color: #db4132;
}

.signs-icon {
  font-size: 83px;
  margin-top: auto;
  align-self: flex-end;
}

.error-icon,
.envelopes-icon {
  font-size: 105px;
}

#success-check.check-icon,
#failure-cross.cross-icon {
  font-size: 48px;
}

.submission-message {
  font-size: 20px;
}

#eligibility-check.check-icon {
  font-size: 25px;
  padding-right: 12px;
  color: green;
}

.message-container {
  max-width: 435px;
  padding-bottom: 7px;
}

.message-container .header {
  font-size: 34px;
}

.message-container .message {
  font-size: 18px;
  color: rgba(0, 0, 0, .6);
}

.message-container .header,
.message-container .message .barcode {
  color: rgba(0, 0, 0, .75);
}

.wrapper-container #post-office-redirection #special-locations-dropdown {
  width: 100%;
  max-width: 600px;
  padding: 5px;
  font-size: 16px;
  margin-top: 14px;
  color: #000000;
  border-radius: 5px;
  border: 1px solid #000000;
  appearance: auto;
}

.wrapper-container #post-office-redirection #special-locations-dropdown:focus {
  border-color: #000000;
  color: #000000; 
  outline: none;
}

.wrapper-container #post-office-redirection .special-locations-dropdown-wrapper .missing-special-location-error-message-wrapper {
  display: grid;
  font-size: 16px;
  color: #db4132;
  column-gap: 11px;
  padding-top: 20px;
  align-items: center;
  grid-template-columns: auto 1fr;
}

.delivery-options-container {
  width: 100%;
  padding-top: 38px;
}

.delivery-options-container .delivery-options { 
  padding-top: 20px;
  padding-bottom: 20px;
}

.delivery-options-container .delivery-options .delivery-option-wrapper {
  display: flex;
  padding: 20px;
  column-gap: 20px;
  border-radius: 13px;
  border: 0.5px rgba(0, 0, 0, .1) solid;  
}

.delivery-options-container .delivery-options .delivery-option-wrapper .address {
  display: flex;
  padding-top: 2px;
  color: rgba(0, 0, 0, .6);
}

.delivery-options-container .delivery-options .delivery-option-wrapper .address-info {
  padding-top: 12px;
  font-size: 16px;
}

.delivery-options-container .note {
  font-size: 16px;
  padding-top: 5px;
  color: #000000;
  font-style: italic;
}

.delivery-options-container .note-emphasized {
  font-size: 18px;
  color: #000000;
}

.delivery-options-container .note-emphasized.collection-availability-note {
  display: grid;
  padding: 20px;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 20px;
  border-radius: 13px;
  border: 0.5px rgba(0, 0, 0, .1) solid;  
}

.delivery-options-container .note-emphasized.collection-availability-note .info-icon-wrapper {
  height: 27px;
}

.delivery-options-container .note-emphasized.collection-availability-note .info-icon-wrapper .info-icon {
  font-size: 27px;
  color: #1434A4;
}

.eligibility-text-container {
  width: 100%;
  display: flex;
  padding: 20px;
  border-radius: 13px;
  align-items: center;
  flex-direction: row;
  border: 1px #db4132 solid;
}

.condition-text-container {
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: row;
  color: orange;
  font-size: 16px;
}

.delivery-option-label {
  width: 100%;
  display: flex;
  padding: 10px;
}

.delivery-option-title-text {
  margin-bottom: 10px;
}

.recipient-verification-title-text {
  font-size: 18px;
  color: black;
}

.recipient-verification-body-text {
  color: black !important;
  /* overwrite the Vue style */
}

.recipient-verification-barcode-line {
  margin-top: 10px;
  font-size: 16px;
}

.recipient-verification-name-line {
  margin-top: 10px;
  font-size: 14px;
}

.recipient-verification-destination-line {
  margin-bottom: 10px;
  font-size: 14px;
}

.recipient-verification-question-line {
  font-size: 16px
}

.recipient-verification-button-no {
  color: white;
}

.confirmation-for-no-title-text {
  font-size: 18px;
  color: black;
}

.confirmation-for-no-body-text {
  color: black !important;
  /* overwrite the Vue style */
}

.confirmation-for-no-details {
  margin-top: 10px;
  font-size: 16px
}

.black-anchor {
  color:#000000;
}
</style>